import { useGame } from '@/contexts/GameContext';
import { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';

interface PerformanceStats {
  fps: number;
  frameTime: number;
  memory?: number;
}

export function PerformanceOptimizer({ showMonitor = false }: { showMonitor?: boolean }) {
  const { gameState, updateGameState } = useGame();
  const [stats, setStats] = useState<PerformanceStats>({ fps: 60, frameTime: 16.67 });
  const [autoOptimized, setAutoOptimized] = useState(false);
  
  const frameRef = useRef<number>(0);
  const lastTimeRef = useRef<number>(performance.now());
  const fpsHistoryRef = useRef<number[]>([]);
  const lowFPSCountRef = useRef<number>(0);
  
  // Apply performance classes
  useEffect(() => {
    const graphics = gameState.gameSettings.graphics;
    const body = document.body;
    
    // Apply low-graphics class based on settings
    if (graphics === 'low') {
      body.classList.add('low-graphics');
    } else {
      body.classList.remove('low-graphics');
    }
    
    if (graphics === 'medium') {
      body.classList.add('medium-graphics');
    } else {
      body.classList.remove('medium-graphics');
    }
    
    // Apply effects disabled class
    if (!gameState.gameSettings.effects) {
      body.classList.add('effects-disabled');
    } else {
      body.classList.remove('effects-disabled');
    }
    
    // Apply GPU acceleration hints
    const gameContainer = document.getElementById('game-container');
    if (gameContainer) {
      gameContainer.style.willChange = 'transform';
      gameContainer.style.transform = 'translateZ(0)';
    }
    
    return () => {
      body.classList.remove('low-graphics', 'medium-graphics', 'effects-disabled');
    };
  }, [gameState.gameSettings.graphics, gameState.gameSettings.effects]);
  
  // Auto-optimize based on FPS
  const autoOptimize = useCallback(() => {
    if (autoOptimized) return;
    
    console.warn('Auto-optimizing graphics due to low FPS');
    setAutoOptimized(true);
    
    // Downgrade graphics settings
    const currentGraphics = gameState.gameSettings.graphics;
    if (currentGraphics === 'high') {
      updateGameState({
        gameSettings: {
          ...gameState.gameSettings,
          graphics: 'medium',
        },
      });
    } else if (currentGraphics === 'medium') {
      updateGameState({
        gameSettings: {
          ...gameState.gameSettings,
          graphics: 'low',
          effects: false,
        },
      });
    }
  }, [gameState.gameSettings, updateGameState, autoOptimized]);
  
  // FPS monitoring
  useEffect(() => {
    if (!showMonitor && !gameState.gameSettings.effects) return;
    
    let animationId: number;
    
    const measurePerformance = (currentTime: number) => {
      const deltaTime = Math.max(1, currentTime - lastTimeRef.current);
      const rawFPS = 1000 / deltaTime;
      const currentFPS = Number.isFinite(rawFPS) && rawFPS > 0 ? Math.min(rawFPS, 240) : 60;
      
      // Add to history
      fpsHistoryRef.current.push(currentFPS);
      if (fpsHistoryRef.current.length > 60) {
        fpsHistoryRef.current.shift();
      }
      
      // Update every 30 frames
      frameRef.current++;
      if (frameRef.current % 30 === 0) {
        const finiteHistory = fpsHistoryRef.current.filter(value => Number.isFinite(value) && value > 0);
        const avgFPS =
          finiteHistory.length > 0
            ? finiteHistory.reduce((a, b) => a + b, 0) / finiteHistory.length
            : 60;
        
        // Check for consistently low FPS
        if (avgFPS < 30) {
          lowFPSCountRef.current++;
          if (lowFPSCountRef.current > 5) {
            autoOptimize();
          }
        } else {
          lowFPSCountRef.current = 0;
        }
        
        setStats({
          fps: Math.round(avgFPS),
          frameTime: Math.round((1000 / avgFPS) * 100) / 100,
          memory: (performance as any).memory ? 
            Math.round((performance as any).memory.usedJSHeapSize / 1048576) : 
            undefined,
        });
      }
      
      lastTimeRef.current = currentTime;
      animationId = requestAnimationFrame(measurePerformance);
    };
    
    animationId = requestAnimationFrame(measurePerformance);
    
    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [showMonitor, gameState.gameSettings.effects, autoOptimize]);
  
  // FPS color indicator
  const getFPSColor = (fps: number) => {
    if (fps >= 55) return '#4CAF50';
    if (fps >= 30) return '#FFC107';
    return '#F44336';
  };
  
  if (!showMonitor) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, x: 100 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 100 }}
        style={{
          position: 'fixed',
          top: 10,
          right: 10,
          background: 'rgba(0, 0, 0, 0.8)',
          color: '#fff',
          padding: '10px 15px',
          borderRadius: '8px',
          fontFamily: 'monospace',
          fontSize: '12px',
          zIndex: 10000,
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          pointerEvents: 'none',
        }}
      >
        <div style={{ marginBottom: '5px' }}>
          <span style={{ color: getFPSColor(stats.fps), fontWeight: 'bold' }}>
            {stats.fps} FPS
          </span>
          <span style={{ color: '#999', marginLeft: '10px' }}>
            ({stats.frameTime}ms)
          </span>
        </div>
        
        {stats.memory !== undefined && (
          <div style={{ color: '#888' }}>
            Memory: {stats.memory}MB
          </div>
        )}
        
        <div style={{ color: '#888', fontSize: '10px', marginTop: '5px' }}>
          Graphics: {gameState.gameSettings.graphics}
          {autoOptimized && ' (auto)'}
        </div>
        
        {/* FPS Graph */}
        <div
          style={{
            marginTop: '8px',
            height: '30px',
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '4px',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <svg width="150" height="30" style={{ position: 'absolute' }}>
            <polyline
              points={fpsHistoryRef.current
                .map((fps, i) => {
                  const safeFPS = Number.isFinite(fps) && fps > 0 ? Math.min(fps, 120) : 0;
                  const x = (i / 60) * 150;
                  const y = Math.max(0, Math.min(30, 30 - (safeFPS / 60) * 25));
                  return `${x},${y}`;
                })
                .join(' ')}
              fill="none"
              stroke={getFPSColor(stats.fps)}
              strokeWidth="1"
              opacity="0.8"
            />
          </svg>
        </div>
        
        {stats.fps < 30 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            style={{
              marginTop: '8px',
              fontSize: '10px',
              color: '#FFC107',
            }}
          >
            Low FPS detected
          </motion.div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}

// Performance optimization hooks
export const usePerformanceOptimization = () => {
  const [shouldReduceMotion, setShouldReduceMotion] = useState(false);
  const { gameState } = useGame();
  
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setShouldReduceMotion(mediaQuery.matches);
    
    const handleChange = (e: MediaQueryListEvent) => {
      setShouldReduceMotion(e.matches);
    };
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);
  
  return {
    shouldReduceMotion,
    graphicsQuality: gameState.gameSettings.graphics,
    optimizedMotionProps: shouldReduceMotion ? 
      { initial: false, animate: true, transition: { duration: 0 } } : 
      {},
  };
};
